import { UIIcon } from '../../../config/icons';
import { YDataSource, YDataSourceObserver } from '../../y/data-source';
import { $log } from '../../y/debug';
import { yLifetimeTracked } from '../../y/decorators';
import { $on } from '../../y/events';
import { YInputOptions } from '../../y/form';
import { $storage } from '../../y/storage';
import { uiAnimate } from '../animate';
import { onAttach, onDetach } from '../attach';
import { $el, UILink } from '../elements';
import { $fmt, UIValueFormat } from '../fmt';
import { UIOrderDelegate, uiStartOrder } from '../order';
import { onInteract } from '../touch';
import { UIAlignment, UIClassesSpec, UIPoint, UIRelPos, UIValue } from '../types';
import { UIView, UIViewAddSpec, UIViewSpec } from '../view';
import { UIActionResult } from './button';
import { UIMultiSelect } from './select';
import { UICheckbox } from './toggle';
import { addTooltip } from './tooltip';

export interface UITableButton<Model, Controller extends UITableRowController<Model>> {
    icon?: UIIcon;
    title?: string;
    when?: boolean | ((item: Model) => boolean);
    autohide?: boolean;
    action?: ((item: Model, row: UITableRow<Model, Controller>) => UIActionResult) | 'expand';
    link?: (item: Model, row: UITableRow<Model, Controller>) => (UILink | undefined);
    tooltip?: string;
    classes?: (item: Model, row: UITableRow<Model, Controller>) => string;
}

declare global {
    interface HTMLElement {
        ui_table_row?: UITableRow<any, any>;
        ui_table_button?: UITableButton<any, any>;
    }
}

interface UITableColumnOptions<Model, Controller extends UITableRowController<Model>> {
    id?: string;
    optional?: boolean;
    alignment?: UIAlignment;
    title?: string;
    width?: number;
    wide_only?: boolean;
    classes?: UIClassesSpec;
    header_classes?: UIClassesSpec;
    decorator?: (td: HTMLTableCellElement, row: UITableRow<Model, Controller>) => void;
    comparator?: (lhs: Model, rhs: Model, order: number) => number;
    header?: () => HTMLElement;
    on_click?: (row: UITableRow<Model, Controller>, td: HTMLTableCellElement) => void;
}

interface UITableValueColumnOptions<Model, Controller extends UITableRowController<Model>, Value> extends UITableColumnOptions<Model, Controller> {
    link?: (item: Model) => UILink;
    sort?: boolean | ((item: Model) => Value);
    required?: boolean;
}

interface UITableButtonColumnOptions<Model, Controller extends UITableRowController<Model>> extends UITableColumnOptions<Model, Controller> {
}

interface UITableCheckboxColumnOptions<Model, Controller extends UITableRowController<Model>> {
    select_all?: boolean;
    when?: (item: Model) => boolean;
    expansion?: (td: HTMLTableCellElement, row: UITableRow<Model, Controller>) => void;
}

export interface UITableRowController<Model> {
    commit?(this: UITableRowController<Model>): void;
    expand?(this: UITableRowController<Model>): UIViewSpec | undefined;
    value_expand?(this: UITableRowController<Model>): Model[] | undefined;
    unexpand?(this: UITableRowController<Model>): void; // TODO: maybe allow blocking the unexpand
}

interface UITableColumn<Model, Controller extends UITableRowController<Model>> {
    id?: string;
    optional?: boolean;
    width: number;
    wide_only: boolean;
    classes?: UIClassesSpec;
    title?: string;
    cell: (row: UITableRow<Model, Controller>) => UIViewSpec;
    header_cell?: (head: UITableHead<Model, Controller>) => UIViewSpec;
    comparator?: (lhs: Model, rhs: Model, o: number) => number;
    always_sort_down?: boolean;
    on_click?: (row: UITableRow<Model, Controller>, td: HTMLTableCellElement, ev: MouseEvent) => void;
}

function makeButton<Model, Controller extends UITableRowController<Model>>(row: UITableRow<Model, Controller>, b: UITableButton<Model, Controller>) {
    const item = row.item;
    const classes = ['ui-table-button', !b.autohide && 'm-always', b.classes && b.classes(item, row)];
    let link: UILink | undefined;
    if(b.link) {
        link = b.link(item, row);
        if(!link) return undefined;
    }
    let button = b.icon ? $el.ICON(b.icon, classes, link) :
        link ? $el.A(link, b.title, classes) : $el.SPAN(classes, b.title);

    b.tooltip && addTooltip(b.tooltip, row, { hover: button, kind: 'k-table', position: UIRelPos.Right });
    button.ui_table_button = b;

    return button;
}

export class UITableDefinition<Model, Controller extends UITableRowController<Model> = UITableRowController<Model>> {
    columns: UITableColumn<Model, Controller>[] = [];
    preference_prefix?: string;
    controller?: (row: UITableRow<Model, Controller>) => Controller | undefined | void;
    row_classes?: UIClassesSpec;
    row_header_classes?: UIClassesSpec;

    addCustom(options: UITableColumnOptions<Model, Controller>) {
        const alignment = options.alignment;
        const classes = [
            options.classes,
            alignment == UIAlignment.Right && 'm-right',
            alignment == UIAlignment.Center && 'm-center',
            options.wide_only && 'q-w'
        ];

        this.columns.push({
            id: options.id,
            optional: options.optional,
            title: options.title,
            width: options.width || 100,
            wide_only: !!options.wide_only,
            classes,
            cell: row => {
                const td = $el.create('td', classes);
                options.decorator && options.decorator(td, row);
                return td;
            },
            comparator: options.comparator,
            on_click: options.on_click
        });
    }

    addValue(value: (m: Model) => string, options: UITableValueColumnOptions<Model, Controller, string>): void
    addValue<Value>(value: (m: Model) => Value, format: UIValueFormat<Value>, options: UITableValueColumnOptions<Model, Controller, Value>): void
    addValue<Value>(value: (m: Model) => Value, format_options: any, maybe_options?: UITableValueColumnOptions<Model, Controller, Value>) {
        const options = maybe_options || format_options as UITableValueColumnOptions<Model, Controller, Value>;
        const format = (maybe_options ? format_options : $fmt.STRING) as UIValueFormat<Value>;
        const alignment = options.alignment || format.alignment;
        let comparator = options.comparator;
        const link = options.link;
        const decorator = options.decorator;

        if(!comparator) {
            const sort = options.sort;
            if(typeof sort == 'function') {
                comparator = (lhs, rhs, o) => format.compare(sort(lhs), sort(rhs), o)
            } else if(sort !== false) {
                comparator = (lhs, rhs, o) => format.compare(value(lhs), value(rhs), o);
            }
        }

        const classes = [
            options.classes,
            alignment == UIAlignment.Right && 'm-right',
            alignment == UIAlignment.Center && 'm-center',
            options.wide_only && 'q-w'
        ];

        this.columns.push({
            id: options.id,
            optional: options.optional,
            title: options.title,
            width: options.width || 100,
            wide_only: !!options.wide_only,
            classes: classes,
            cell: row => {
                const model = row.item;
                let content = format.content(value(model));

                if(link)
                    content = $el.A(link(model), content);

                const cell = $el.create('td', classes, content);
                decorator?.(cell, row);
                return cell;
            },
            comparator,
            on_click: options.on_click
        });
    }

    addButtons(buttons: UITableButton<Model, Controller>[], options: UITableButtonColumnOptions<Model, Controller> = {}) {
        this.columns.push({
            id: options.id,
            title: options.title,
            width: options.width || 100,
            wide_only: false,
            cell: row => {
                const item = row.item;
                const td = $el.create('td', 'r-buttons');

                if(row.body.extra) return td;

                for(let i = 0; i < buttons.length; i++) {
                    const b = buttons[i];
                    if(b.when === false || (typeof b.when === 'function' && !b.when(item))) continue;
                    let button = makeButton(row, b);
                    if(button)
                        td.appendChild(button);
                }

                return td;
            },
            header_cell: head => {
                const cell = $el.create('th');
                const cols = this.columns;
                if(cols.some(c => c.optional)) {
                    cell.classList.add('m-right', 'm-sortable');
                    const button = $el.ICON(UIIcon.GEAR);
                    cell.addEventListener('click', () => {
                        const values: UIValue<number>[] = [];
                        for(let i = 0; i < cols.length; i++) {
                            const col = cols[i];
                            if(col.optional && col.title)
                                values.push({ key: i, name: col.title });
                        }
                        const select = new UIMultiSelect(values, { keep_open: true });
                        select.value = head.visible_columns;
                        $on(select, 'value', v => {
                            head.showColumns(v);
                        });
                        select.showDropdown(button);
                    });
                    cell.ui_add($el.DIV('r-title', button));
                }
                return cell;
            },
            on_click: (row, _td, ev) => {
                const button = (ev.target as HTMLElement).closest('.ui-table-button') as HTMLElement;
                if(!button) return;
                const b = button.ui_table_button!;

                const action = b.action;

                if(action) {
                    if(action == 'expand') {
                        row.expand(true);
                    } else {
                        const result = action(row.item, row);
                        if(result) {
                            if(result instanceof Promise) {
                                button.classList.add('f-spin');
                                result.then(v => {
                                    button.classList.remove('f-spin');
                                    if(v === true) {
                                        button.classList.add('f-okay');
                                        setTimeout(() => { button.classList.remove('f-okay') }, 2000);
                                    }
                                }, () => {
                                    button.classList.remove('f-spin');
                                });
                            } else if(result === true) {
                                button.classList.add('f-okay');
                                setTimeout(() => { button.classList.remove('f-okay') }, 2000);
                            }
                        }
                    }
                }
            }
        })
    }

    addHandle() {
        this.columns.push({
            width: -39,
            wide_only: false,
            cell: row => {
                if(row.parent) return $el.create('td');
                const handle = $el.HANDLE();
                onInteract(handle, e => uiStartOrder(e, row.body.onOrder(row)));
                return $el.create('td', undefined, handle);
            }
        });
    }

    addCheckbox(options: UITableCheckboxColumnOptions<Model, Controller> = {}) {
        const expansion = options.expansion;

        this.columns.push({
            width: -30,
            wide_only: false,
            cell: row => {
                const view = $el.create('td', 'r-checkbox');
                if(!row.parent && !row.body.extra && (!options.when || options.when(row.item))) {
                    const checkbox = row.checkbox = new UICheckbox;
                    checkbox.value = !!row.body.source.isChecked?.(row.item);
                    view.ui_add(checkbox);
                    $on(checkbox, 'value', v => {
                        row.body.source.check?.(row.item, v)
                    });
                } else if(row.parent && expansion) {
                    expansion(view, row);
                }
                return view;
            },
            header_cell: head => {
                const view = $el.create('th', 'r-checkbox');
                if(options.select_all !== false) {
                    const checkbox = new UICheckbox;
                    view.ui_add(checkbox);
                    $on(checkbox, 'value', v => {
                        if(options.when && v) {
                            head.source.checkIf?.(options.when);
                        } else {
                            head.source?.checkAll?.(v);
                        }
                    });
                }
                return view;
            }
        });
    }
}


export interface UITableOptions extends YInputOptions {
    header?: boolean;
    wrapper?: boolean;
    classes?: UIClassesSpec;
    table_element_classes?: UIClassesSpec;
    progressive?: boolean;
    sort?: UITableSort;
}

interface UITableExpansion {
    view_spec: UIViewAddSpec;
    remove(): void;
    expand(): void;
    unexpand(): void;
}

class UITableValueExpansion<Model, Controller extends UITableRowController<Model>> {
    private _rows: UITableRow<Model, Controller>[];
    private _row: UITableRow<Model, Controller>

    constructor(row: UITableRow<Model, Controller>, values: Model[]) {
        this._row = row;
        this._rows = values.map(v => new UITableRow(row.body, v, row));
    }

    get view_spec() {
        return this._rows;
    }

    remove() {
        for(const r of this._rows) {
            r.remove();
        }
    }

    expand() {

    }

    unexpand() {
        this.remove();
        this._row.classList.remove('f-expanded');
    }
}

class UITableViewExpansion<Model, Controller extends UITableRowController<Model>> {
    private _tr: HTMLTableRowElement
    private _div: HTMLDivElement;
    private _row: UITableRow<Model, Controller>

    constructor(row: UITableRow<Model, Controller>, expansion: UIViewSpec) {
        this._row = row;
        const cell = $el.create('td', '', this._div = $el.B(expansion));
        cell.colSpan = row.body.definition.columns.length;
        this._tr = $el.create('tr', 'ui-table-expansion', cell);
    }

    get view_spec() {
        return this._tr;
    }

    remove() {
        this._tr.remove();
    }

    expand() {
        const div = this._div;

        const callback = () => { div.style.height = ''; };

        const height = div.scrollHeight;
        div.style.height = '0';
        requestAnimationFrame(() => {
            uiAnimate([{ what: div, style: 'height', from: 0, to: height, duration: 0.2 }], callback);
        });
    }

    unexpand() {
        const div = this._div;

        const callback = () => {
            this._tr.remove();
            this._row.classList.remove('f-expanded');
        };
        requestAnimationFrame(() => {
            uiAnimate([{ what: div, style: 'height', from: div.scrollHeight, to: 0, duration: 0.2 }], callback);
        });
    }
}

export class UITableRow<Model, Controller extends UITableRowController<Model> = UITableRowController<Model>> extends UIView {
    expansion?: UITableExpansion;

    parent?: UITableRow<Model, Controller>;
    body: UITableBody<Model, Controller>;

    item: Model;
    checkbox?: UICheckbox;
    controller?: Controller | void;

    constructor(body: UITableBody<Model, Controller>, model: Model, parent?: UITableRow<Model, Controller>) {
        const def = body.definition;

        super('tr', ['ui-table-row', def.row_classes]);

        if(parent)
            this.classList.add('ui-table-value-expansion');

        this.body = body;
        this.item = model;
        this.parent = parent;
        this.controller = def.controller?.(this);

        let wants_clicks = false;

        const cols = def.columns;
        const hidden = body.table.head!.hidden;
        for(let i = 0; i < cols.length; i++) {
            const c = cols[i];
            if(c.on_click) wants_clicks = true;
            this.ui_add(hidden[i] ? $el.create('td', 'u-gone') : c.cell(this));
        }

        if(body.table.on_row_selected) {
            wants_clicks = true;
            this.classList.add('u-clickable');
        }

        if(wants_clicks)
            this.root.ui_table_row = this;

        return this
    }

    onRowClicked(ev: MouseEvent) {
        const td = (ev.target as Element).closest('td');

        const children = this.root.childNodes;
        for(let i = 0; i < children.length; i++) {
            if(td == children[i]) {
                const col = this.body.definition.columns[i];
                if(col && col.on_click) {
                    col.on_click(this, td, ev);
                    ev.stopPropagation();
                    return;
                }
                break;
            }
        }

        this.body.table.on_row_selected?.(this);
    }

    reload() {
        this.ui_empty();
        const body = this.body;
        const cols = body.definition.columns;
        const hidden = body.table.head!.hidden;
        for(let i = 0; i < cols.length; i++) {
            this.ui_add(hidden[i] ? $el.create('td', 'u-gone') : cols[i].cell(this));
        }

        if(this.expansion) {
            this.unexpand();
            this.expand();
        }
    }

    assign(item: Model) {
        this.item = item;
        this.reload();
    }

    reloadCell(i: number) {
        const body = this.body;
        this.root.getElementsByTagName('td')[i].ui_replace(body.table.head!.hidden[i] ? $el.create('td', 'u-gone') : body.definition.columns[i].cell(this));
    }

    expand(toggle?: boolean) {
        if(this.parent) return;

        if(this.expansion) {
            if(toggle) this.unexpand();
            return;
        }

        this.body.unexpandAll();

        const controller = this.controller;
        if(!controller) return;

        if(controller.expand) {
            const content = controller.expand();
            if(!content) return;

            this.classList.add('f-expanded');
            const expansion = this.expansion = new UITableViewExpansion(this, content);
            this.body.ui_insert(expansion.view_spec, this, true);
            expansion.expand();
        } else if(controller.value_expand) {
            const values = controller.value_expand();
            if(!values || !values.length) return;

            this.classList.add('f-expanded');
            const expansion = this.expansion = new UITableValueExpansion(this, values);
            this.body.ui_insert(expansion.view_spec, this, true);
            expansion.expand();
        }
    }

    unexpand(removing?: boolean) {
        const expansion = this.expansion;
        if(!expansion) return;

        if(!removing && this.controller && this.controller.unexpand)
            this.controller.unexpand();

        if(removing) {
            expansion.remove();
            this.classList.remove('f-expanded');
        } else {
            expansion.unexpand();
        }

        this.expansion = undefined;
    }

    nextRow() {
        const index = this.body.indexOf(this);
        if(index < 0) return undefined;
        return this.body.getRow(index + 1);
    }

    previousRow() {
        const index = this.body.indexOf(this);
        if(index <= 0) return undefined;
        return this.body.getRow(index - 1);
    }

    get center() {
        return this.root.offsetTop + this.root.offsetHeight / 2;
    }

    get index() {
        return this.body.indexOf(this);
    }
}

type UITableSort = [number, 1 | -1];

class UITableHead<Model, Controller extends UITableRowController<Model>> extends UIView {
    readonly source: YDataSource<Model>;
    readonly definition: UITableDefinition<Model, Controller>;
    readonly hidden: boolean[];

    private _ths: HTMLElement[];
    private _table_elem: HTMLElement;
    private _table: UITable<Model, Controller>
    private _sort?: UITableSort;

    constructor(definition: UITableDefinition<Model, Controller>, source: YDataSource<Model>, table: UITable<Model, Controller>, table_elem: HTMLElement, no_header: boolean) {
        const tr = $el.create('tr', definition.row_header_classes);
        super('thead', ['ui-table-head', no_header && 'm-no-header'], tr);

        this.source = source;
        this.definition = definition;
        this._table = table;
        this._table_elem = table_elem;

        const cols = definition.columns;
        const ths: HTMLElement[] = this._ths = [];
        const hidden: boolean[] = this.hidden = [];

        for(let i = 0; i < cols.length; i++) {
            const col = cols[i];
            const title = $el.SPAN('r-title', col.title)

            const th = col.header_cell?.(this) ?? $el.create('th', col.classes, title);
            const th_elem = th.ui_elem;

            if((col.comparator && source.sortBySpec) || (col.id && source.sortById)) {
                th_elem.classList.add('m-sortable');
                th_elem.addEventListener('click', () => { this._onSort(i) });
                const indicator = $el.DIV('ui-sort-indicator');
                indicator.innerHTML = '<svg class="r-stem" viewBox="0 0 84 84"><rect width="8" height="60" x="38" y="12"/></svg>' +
                    '<svg class="r-left" viewBox="0 0 84 84"><rect width="42" height="8" x="4" y="38"/></svg>' +
                    '<svg class="r-right" viewBox="0 0 84 84"><rect width="42" height="8" x="38" y="38"/></svg>';
                title.appendChild(indicator);
            }

            if(col.wide_only)
                th_elem.classList.add('q-w');

            tr.ui_add(th);
            ths.push(th_elem);
            hidden.push(false);
        }

        if(definition.preference_prefix) {
            const index = $storage.getPreference(definition.preference_prefix + '_sort', -1) as number;
            const order = $storage.getPreference(definition.preference_prefix + '_order', 1) as 1 | -1;
            const visibility = $storage.getPreference(definition.preference_prefix + '_cols', undefined) as (number[] | undefined)
            if(index >= 0 && index < cols.length)
                this.sort = [index, order];
            if(visibility && Array.isArray(visibility))
                this.showColumns(visibility, true);
        }

        this._recalcSize();
    }

    private _recalcSize() {
        let total_fixed = 0;
        let total_var = 0;
        let total_var_always = 0;

        const cols = this.definition.columns;
        const hidden = this.hidden;

        for(let i = 0; i < cols.length; i++) {
            if(hidden[i]) continue;
            const col = cols[i];
            if(col.width < 0) {
                if(!col.wide_only)
                    total_fixed -= col.width;
            } else {
                total_var += col.width;
                if(!col.wide_only)
                    total_var_always += col.width;
            }
        }

        const ths = this._ths;

        for(let i = 0; i < cols.length; i++) {
            const col = cols[i];
            const th = ths[i];

            if(col.width < 0) {
                th.style.width = -col.width + 'px';
                th.style.minWidth = -col.width + 'px';
            } else {
                th.style.width = (col.width / total_var * 100) + '%';
            }
        }

        if(total_var) {
            this._table_elem.style.minWidth = `${total_fixed + total_var_always}px`;
        } else {
            this._table_elem.style.minWidth = `${total_fixed}px`;
            this._table_elem.style.width = `${total_fixed}px`;
        }
    }

    get sort() {
        return this._sort;
    }

    set sort(sort: UITableSort | undefined) {
        const current = this._sort;
        const def = this.definition;
        if(sort == current || (sort && current && sort[0] == current[0] && sort[1] == current[1])) return;

        if(current) {
            this._ths[current[0]].querySelector('.ui-sort-indicator')?.classList.remove('f-sorted', 'f-up');
        }

        if(sort) {
            const col = def.columns[sort[0]];
            const indicator = this._ths[sort[0]].querySelector('.ui-sort-indicator');
            if(indicator) {
                indicator.classList.add('f-sorted');
                if(sort[1] == -1 && !col.always_sort_down)
                    indicator.classList.add('f-up');
            }

            if(def.preference_prefix) {
                $storage.setPreference(def.preference_prefix + '_sort', sort[0]);
                $storage.setPreference(def.preference_prefix + '_order', sort[1]);
            }

            const source = this.source;
            if(col) {
                if(source.sortBySpec && col.comparator)
                    source.sortBySpec([col.comparator, sort[1]]);
                else if(source.sortById && col.id)
                    source.sortById(col.id, sort[1]);
            }
        }

        this._sort = sort;
    }

    private _onSort(column: number) {
        const current = this._sort;
        this.sort = [column, current && column == current[0] ? (-current[1] as (1 | -1)) : 1];
    }

    showColumns(is: number[], dont_recalc?: boolean) {
        const definition = this.definition;
        const cols = definition.columns;
        const ths = this._ths;
        const hidden = this.hidden;
        const bodies = this._table.bodies;
        let needs_recalc = false;

        for(let i = 0; i < cols.length; i++) {
            if(cols[i].optional) {
                const h = !is.includes(i);
                if(h != hidden[i]) {
                    hidden[i] = h;
                    ths[i].classList.toggle('u-gone', h);
                    for(const b of bodies) {
                        b.reloadCol(i);
                    }
                    needs_recalc = true;
                }
            }
        }

        if(definition.preference_prefix)
            $storage.setPreference(definition.preference_prefix + '_cols', is);

        if(needs_recalc && !dont_recalc)
            this._recalcSize();
    }

    get visible_columns() {
        const cols = this.definition.columns;
        const hidden = this.hidden;
        const is: number[] = [];

        for(let i = 0; i < cols.length; i++) {
            if(cols[i].optional && !hidden[i])
                is.push(i);
        }

        return is;
    }
}

@yLifetimeTracked
class UITableBody<Model, Controller extends UITableRowController<Model>> extends UIView implements YDataSourceObserver<Model> {
    readonly table: UITable<Model, Controller>;
    readonly definition: UITableDefinition<Model, Controller>;
    readonly source: YDataSource<Model>;
    readonly extra: boolean;

    private _rows: UITableRow<Model, Controller>[];
    private _handler?: (e: Event) => void;
    private _added: number;
    private _wanted: number;
    private _add_all: boolean;

    constructor(definition: UITableDefinition<Model, Controller>, source: YDataSource<Model>, table: UITable<Model, Controller>, extra: boolean, add_all: boolean) {
        super('tbody', 'ui-table-body');

        this.table = table;
        this.definition = definition;
        this.source = source;
        this._rows = [];
        this.extra = extra;

        let down_x: number, down_y: number;
        this.root.addEventListener('mousedown', ev => {
            down_x = ev.pageX;
            down_y = ev.pageY;
        });

        this.root.addEventListener('click', ev => {
            const dist = (ev.pageX - down_x) * (ev.pageX - down_x) + (ev.pageY - down_y) * (ev.pageY - down_y);
            if(dist > 15) return;
            const target = ev.target as HTMLElement;
            if(target.closest('.ui-table-body') != this.root) return;
            const row = (target.closest('.ui-table-row') as HTMLTableRowElement).ui_table_row;
            if(!row) return;
            row.onRowClicked(ev);
        });

        // TODO: implement instead some OnWindowScroll funtionality, which is triggered only for attached nodes
        if(!add_all) {
            onAttach(this, () => {
                window.addEventListener('scroll', this._handler = () => {
                    if((this._wanted > this._added || this._add_all) && !source.can_load_more) return;

                    const rect = this.root.getBoundingClientRect();
                    if(rect.bottom < 3 * window.innerHeight) {
                        this._wanted = this._added + 80;
                        this._fillUp();
                    }
                });
            });

            onDetach(this, () => {
                if(this._handler) {
                    window.removeEventListener('scroll', this._handler);
                    this._handler = undefined;
                }
            });
        }

        this._added = 0;
        this._add_all = add_all;
        this._wanted = 80;
        source.addObserver(this);
        this._fillUp();
    }

    private _empty() {
        $log('table', 'emptying');
        this.ui_empty();
        this._added = 0;
        this._wanted = 80;
        this._rows = [];
    }

    private _fill(at: number, count: number) {
        $log('table', 'filling', at, count);
        const source = this.source;

        const new_rows: UITableRow<Model, Controller>[] = [];
        for(let i = 0; i < count; i++) {
            new_rows.push(new UITableRow(this, source.at(at + i)));
        }

        this.ui_insert(new_rows, this._rows[at]);
        this._rows.splice(at, 0, ...new_rows);
        this._added += count;
    }

    private _fillUp() {
        $log('table', 'filling up');
        const source = this.source;

        const count = (this._add_all ? source.count : Math.min(this._wanted, source.count)) - this._added;
        if(count > 0)
            this._fill(this._added, count);

        if(this._wanted > source.count && source.can_load_more)
            source.loadMore();
    }

    private _remove(at: number, count: number) {
        $log('table', 'removing', at, count);
        for(let r of this._rows.splice(at, count)) {
            r.remove();
        }
        this._added -= count;
    }

    unexpandAll() {
        for(const r of this._rows) {
            if(r.expansion)
                r.unexpand();
        }
    }

    reloadCol(index: number) {
        for(const r of this._rows) {
            r.reloadCell(index);
        }
    }

    reloadAll() {
        for(const r of this._rows) {
            r.reload();
        }
    }

    indexOf(row: UITableRow<Model, Controller>) {
        return this._rows.indexOf(row);
    }

    getRow(index: number): UITableRow<Model, Controller> | undefined {
        return this._rows[index];
    }

    onOrder(row: UITableRow<Model, Controller>): UIOrderDelegate | undefined {
        const initial_index = this._rows.indexOf(row);
        if(initial_index < 0) return;
        let last_index = initial_index;

        const centers = this._rows.map(r => r.center);
        const row_elem = row.ui_elem;
        const row_height = row_elem.offsetHeight;
        const initial_pos = row_elem.offsetTop;
        let placeholder: HTMLTableRowElement;

        function calcIndex(top: number, bottom: number) {
            for(let i = 0; i < initial_index; i++) {
                if(top < centers[i])
                    return i;
            }

            for(let i = centers.length - 1; i > initial_index; i--) {
                if(bottom > centers[i])
                    return i + 1;
            }

            return initial_index;
        }

        return {
            orderStart: () => {
                const widths: number[] = [];
                const children = row_elem.children as HTMLCollectionOf<HTMLTableCellElement>;

                for(let i = 0; i < children.length; i++) {
                    widths.push(children[i].offsetWidth);
                }
                row_elem.style.width = row_elem.offsetWidth + 'px';
                for(let i = 0; i < children.length; i++) {
                    children[i].style.width = widths[i] + 'px';
                }

                const cell = $el.create('td');
                cell.colSpan = this.definition.columns.length;
                placeholder = $el.create('tr', 'ui-table-placeholder', cell);
                placeholder.style.height = row_height + 'px';

                row.classList.add('f-dragging');
                this.ui_insert(placeholder, row);
            },
            orderMove: (offset: UIPoint) => {
                const pos = Math.max(this.root.offsetTop, Math.min(this.root.offsetTop + this.root.offsetHeight - row_height, offset[1] + initial_pos));
                row_elem.style.top = pos + 'px';

                const new_index = calcIndex(pos, pos + row_height);

                if(new_index !== last_index) {
                    last_index = new_index;
                    this.ui_insert(placeholder, this._rows[new_index]);
                }

                return row_elem.getBoundingClientRect();
            },
            orderEnd: () => {
                row.classList.remove('f-dragging');
                row_elem.style.width = '';
                row_elem.style.top = '';
                const children = row_elem.children as HTMLCollectionOf<HTMLTableCellElement>;
                for(let i = 0; i < children.length; i++) {
                    children[i].style.width = '';
                }
                placeholder.remove();

                if(last_index != initial_index) {
                    this.ui_insert(row, this._rows[last_index]);

                    const insert_pos = last_index < initial_index ? last_index : last_index - 1;
                    // this._models.splice(this._sort_last_index!, 0, this._models.splice(this._sort_initial_index!, 1)[0]);
                    this._rows.splice(insert_pos, 0, this._rows.splice(initial_index, 1)[0]);
                }
            }
        }
    }

    onItemsInserted(indices: number[], _source: YDataSource<Model>) {
        $log('table', 'onItemsInserted', indices);
        for(const i of indices) {
            if(i >= this._wanted && i >= this._added && !this._add_all) return;
            this._fill(i, 1);
        }
    }

    onItemsAdded(index: number, count: number) {
        $log('table', 'onItemsAdded', index, count);
        if(index >= this._wanted && index >= this._added && !this._add_all) return;
        if(index < this._added)
            this._fill(index, count);
        else
            this._fillUp();
    }

    onItemsRemoved(index: number, count: number) {
        if(index >= this._added) return;
        if(index + count > this._added)
            count = this._added - index;
        this._remove(index, count);
        this._fillUp();
    }

    onItemMoved(_from: number, _to: number, _source: YDataSource<Model>) {

    }

    onItemUpdated(_index: number, _source: YDataSource<Model>) {

    }

    onItemsSorted(mapping: number[], source: YDataSource<Model>) {
        const old_rows = this._rows;
        const num = this._added;
        const rows: UITableRow<Model, Controller>[] = [];
        for(let i = 0; i < num; i++) {
            const n = mapping[i];
            rows.push(n < num ? old_rows[n] : new UITableRow(this, source.at(i)));
        }
        this.ui_empty(this._rows = rows);
    }

    onItemsChecked(source: YDataSource<Model>) {
        for(const r of this._rows) {
            if(r.checkbox)
                r.checkbox.value = !!source.isChecked?.(r.item);
        }
    }

    onItemsWillRead() {
        for(const r of this._rows) {
            r.controller?.commit?.();
        }
    }

    onItemsChanged() {
        this._empty();
        this._fillUp();
    }
}

export class UITable<Model, Controller extends UITableRowController<Model> = UITableRowController<Model>> extends UIView {
    private _table: HTMLElement;
    private _options: UITableOptions;
    private _definition!: UITableDefinition<Model, Controller>
    private _head?: UITableHead<Model, Controller>;
    private _body?: UITableBody<Model, Controller>;
    private _bodies: Set<UITableBody<Model, Controller>>;

    on_row_selected?: (row: UITableRow<Model, Controller>) => void;

    constructor(definition: UITableDefinition<Model, Controller>, source: YDataSource<Model>, options?: UITableOptions)
    constructor(options?: UITableOptions)
    constructor(definition: any, source?: YDataSource<Model>, opts?: UITableOptions) {
        const options = ((source ? opts : definition) || {}) as UITableOptions;

        if(options.wrapper === false) {
            super('table', ['ui-table ui-table-element', options.classes, options.table_element_classes]);
            this._table = this.root;
        } else {
            super('div', ['ui-table ui-table-wrapper', options.classes]);
            this.ui_add(this._table = $el.create('table', ['ui-table-element', options.table_element_classes]));
        }

        this._options = options;
        this._bodies = new Set;

        if(source)
            this.reset(definition, source);
    }

    reset(definition: UITableDefinition<Model, Controller>, source: YDataSource<Model>) {
        this._definition = definition;

        const table = this._table;
        const options = this._options;

        const head = this._head = new UITableHead(definition, source, this, table, options.header === false);
        if(options.sort)
            head.sort = options.sort;
        const body = this._body = new UITableBody(definition, source, this, false, options.progressive === false);
        this._bodies = new Set([body]);

        table.ui_empty([head, body]);
    }

    reloadAll() {
        this._body?.reloadAll();
    }

    get bodies() {
        return [...this._bodies];
    }

    get head() {
        return this._head;
    }

    addExtra(source: YDataSource<Model>) {
        const extra = new UITableBody<Model, Controller>(this._definition, source, this, true, true);
        this._table.ui_add(extra);
        this._bodies.add(extra);
        return extra;
    }

    removeExtra(body: UITableBody<Model, Controller>) {
        this._bodies.delete(body);
        body.remove();
    }
}
